/****************************************************
 * Module Name:  showCriteriaStatsModal.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impexs
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Constants from "../../utility/constants";
import { Box, Button, CircularProgress, FormControlLabel, styled, Switch, Tooltip, Typography } from "@mui/material";
import logo from '../../images/pansuriya.jpg';
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CommonSnackbar from "../common/commonSnackBar";
import Utility from "../../utility/utility";
import NivodaUtilityProxy from "../../utility/proxy/nivodautility/nivodautilityProxy";
import renderCellExpand from "../common/dataGridCellExpand";
const PrioritySwitch = styled((props) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                backgroundColor: theme.palette.mode === 'dark' ? '#2ECA45' : '#65C466',
                opacity: 1,
                border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
            },
        },
        '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: '#33cf4d',
            border: '6px solid #fff',
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
            color:
                theme.palette.mode === 'light'
                    ? theme.palette.grey[100]
                    : theme.palette.grey[600],
        },
        '&.Mui-disabled + .MuiSwitch-track': {
            opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 22,
        height: 22,
    },
    '& .MuiSwitch-track': {
        borderRadius: 26 / 2,
        backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 500,
        }),
    },
}));

const ShowCriteriaStatsModal = ({ show, handleClose }) => {

    const [criteriaStats, setCriteriaStats] = useState([]);
    const [criteriaStatsLoader, setCriteriaStatsLoader] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);


    const [showPriorityWise, setShowPriorityWise] = useState(true);
    const [resultData, setResultData] = useState([]);
    const [filterValues, setFilterValues] = useState({});

    useEffect(() => {
        handleStats();
    }, ([]));

    useEffect(() => {
        if (criteriaStats.length > 0)
            handlePriorityWise();
    }, ([criteriaStats]));

    useEffect(() => {
        if (criteriaStats.length > 0)
            handlePriorityWise();
    }, ([showPriorityWise]));

    const handlePriorityWise = () => {
        setCriteriaStatsLoader(true);
        if (showPriorityWise) {
            setResultData(criteriaStats);
            setCriteriaStatsLoader(false);
        } else {
            const statusWiseMap = new Map();

            criteriaStats.forEach(element => {
                if (statusWiseMap.has(element.status)) {
                    statusWiseMap.set(element.status, statusWiseMap.get(element.status) + element.value);
                } else {
                    statusWiseMap.set(element.status, element.value);
                }
            });

            const result = Array.from(statusWiseMap, ([status, value]) => ({ status, value }));

            setResultData(result);
            setCriteriaStatsLoader(false);
        }
    }

    const handleStats = () => {
        setCriteriaStatsLoader(true);
        NivodaUtilityProxy.GetCriteriaStats((responseData) => {
            if (responseData) {
                const uniqueFilterValues = {
                    status: Utility.getDistinctOptions(responseData, 'status'),
                }
                setFilterValues(uniqueFilterValues);
                setCriteriaStats(responseData);
                setResultData(responseData);
                setCriteriaStatsLoader(false);
            } else {
                setErrorSnackbar(true);
                setCriteriaStatsLoader(false);
            }
        })
    }

    const initialColumns = () => [
        {
            field: "status",
            headerName: "STATUS",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.status : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand
        },
        {
            field: "priority",
            headerName: "PRIORITY",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 120,
            renderCell: (params) => params.value,
        },
        {
            field: "value",
            headerName: "COUNT",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 130,
            renderCell: (params) => Utility.NumberToLocaleString(params.value)
        }
    ];

    const rows = resultData.map((row, index) => ({ ...row, id: index + 1 }));
    const columnNames = !showPriorityWise ? initialColumns().filter(col => col.field !== "priority") : [...initialColumns()];

    return (
        <div>
            <CommonSnackbar
                show={errorSnackbar}
                message={
                    <Box>
                        Criteria Stats Response Fetch Failed
                        <br /> Please try again later.
                    </Box>
                }
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                handleClose={() => setErrorSnackbar(false)}
                title="Response Error"
            />

            <Modal
                show={show}
                onHide={handleClose}
                backdrop="static"
                keyboard={true}
                size="lg"
                enforceFocus={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={logo}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;Criteria Stats
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Box px={1} pt={1} sx={{
                        position: "relative",
                    }}>
                        <Box sx={{ height: 400, width: '100%' }}>
                            <DataGridPro
                                loading={criteriaStatsLoader}
                                rows={rows}
                                columns={columnNames}
                                disableRowSelectionOnClick={true}
                                rowSelection
                                density="compact"
                                slots={{ toolbar: GridToolbar }}
                                slotProps={{
                                    toolbar: () => null,
                                }}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        fontSize: '16px',
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "4px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },

                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                            />
                        </Box>
                        <Box sx={{
                            position: "absolute",
                            top: "0.05rem",
                            right: "1rem",
                            display: "flex",
                            alignItems: "center",
                        }}>
                            <Box display={"flex"} alignItems={"center"}
                                sx={{
                                    "& .MuiFormControlLabel-root": {
                                        margin: 0
                                    },
                                    "& .MuiSwitch-root": {
                                        width: "38px",
                                        height: "22px"
                                    },
                                    "& .MuiSwitch-thumb": {
                                        width: "18px !important",
                                        height: "18px !important"
                                    },
                                    margin: "8px"

                                }}
                            >
                                <FormControlLabel
                                    control={<PrioritySwitch sx={{ m: 1 }} />}
                                    onChange={() => setShowPriorityWise(!showPriorityWise)}
                                    checked={showPriorityWise}
                                />
                                <Typography
                                    sx={{
                                        fontSize: "15px",
                                    }}
                                > Priority Wise</Typography>
                            </Box>
                            {criteriaStatsLoader ? (
                                <CircularProgress size="1rem" />
                            ) : (
                                <Tooltip title="Reload List">
                                    <RefreshOutlinedIcon
                                        onClick={() =>
                                            handleStats()
                                        }
                                        color="primary"
                                        fontSize="medium"
                                        sx={{ cursor: "pointer" }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="contained" color="error" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ShowCriteriaStatsModal;
